import { isNotBookable } from "utils/carPrice";
import { inAnyClass } from "utils/fleetClasses";

/**
 * 1NOW-11129: the Car type and price filters, applied together.
 *
 * Design B ran them in two separate effects that each filtered the FULL list
 * and set the result, so whichever ran last overwrote the other: moving the
 * price slider dropped the ticked class, and the class stayed ticked.
 *
 * It also mixed two price fields (`price >= min && avg_car_price_per_day <=
 * max`) and took the slider's top from `price`. One field now: the average per
 * day for the searched dates, which is the number the card shows. A car whose
 * date-specific price is above every base price is still inside the slider.
 */
export type FilterCar = {
  avg_car_price_per_day?: number | null;
  price?: number | string | null;
  price_on_request?: boolean | null;
  fleet_class?: { name?: string | null } | null;
  fleet_classes?: unknown;
};

export const filterPrice = (car: FilterCar | null | undefined): number | null => {
  const p = car?.avg_car_price_per_day;
  return p === null || p === undefined || !Number.isFinite(Number(p)) ? null : Number(p);
};

/** The slider's top: the dearest car by the same price the filter compares. Rounded up so a $45.50 car is not left above a whole-dollar slider. */
export const maxFilterPrice = (cars: FilterCar[] | null | undefined): number =>
  Math.ceil((cars ?? []).reduce((top, car) => Math.max(top, filterPrice(car) ?? 0), 0));

export const filterFleet = <T extends FilterCar>(
  cars: T[] | null | undefined,
  selectedTypes: string[],
  rangePrices: number[],
): T[] =>
  (cars ?? []).filter((car) => {
    if (selectedTypes.length > 0 && !inAnyClass(car, selectedTypes)) return false;
    // A car on request or with no price stays listed: its card asks for a
    // quote or says a price is coming. It is still held to the class.
    if (isNotBookable(car)) return true;
    const price = filterPrice(car);
    return price !== null && price >= rangePrices[0] && price <= rangePrices[1];
  });
